/**
 * useResizablePanels - Hook for managing resizable panel widths
 *
 * Features:
 * - Persists panel widths to localStorage
 * - Supports collapse/expand with smooth transitions
 * - Min/max constraints for panel widths
 * - Mouse and touch event support
 *
 * @module hooks/useResizablePanels
 */

import { useState, useCallback, useEffect } from 'react';

// Storage key for persisting panel state
const STORAGE_KEY = 'notebook_panel_state';

// Width constraints
const MIN_WIDTH = 200;
const MAX_WIDTH = 500;
const COLLAPSED_WIDTH = 48;

/**
 * Get saved panel state from localStorage
 * @returns {Object|null} Saved state or null
 */
const getSavedState = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    /* ignore parse errors */
  }
  return null;
};

/**
 * Hook for managing resizable panel widths
 *
 * @param {React.RefObject} containerRef - Reference to the container element
 * @param {Object} initialWidths - Initial panel widths
 * @param {number} initialWidths.left - Initial left panel width (default: 280)
 * @param {number} initialWidths.right - Initial right panel width (default: 320)
 * @returns {Object} Panel state and handlers
 */
const useResizablePanels = (containerRef, initialWidths = { left: 280, right: 320 }) => {
  const savedState = getSavedState();

  const [leftWidth, setLeftWidth] = useState(savedState?.leftWidth || initialWidths.left);
  const [rightWidth, setRightWidth] = useState(savedState?.rightWidth || initialWidths.right);
  const [leftCollapsed, setLeftCollapsed] = useState(savedState?.leftCollapsed || false);
  const [rightCollapsed, setRightCollapsed] = useState(savedState?.rightCollapsed || false);

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    const state = { leftWidth, rightWidth, leftCollapsed, rightCollapsed };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [leftWidth, rightWidth, leftCollapsed, rightCollapsed]);

  const handleLeftResize = useCallback(
    clientX => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = clientX - containerRect.left;

      if (newWidth < MIN_WIDTH / 2) {
        setLeftCollapsed(true);
      } else {
        setLeftCollapsed(false);
        setLeftWidth(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth)));
      }
    },
    [containerRef]
  );

  const handleRightResize = useCallback(
    clientX => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = containerRect.right - clientX;

      if (newWidth < MIN_WIDTH / 2) {
        setRightCollapsed(true);
      } else {
        setRightCollapsed(false);
        setRightWidth(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth)));
      }
    },
    [containerRef]
  );

  const toggleLeftCollapse = useCallback(() => {
    setLeftCollapsed(prev => !prev);
  }, []);

  const toggleRightCollapse = useCallback(() => {
    setRightCollapsed(prev => !prev);
  }, []);

  // Explicit expand functions (for programmatic expansion)
  const expandLeft = useCallback(() => {
    setLeftCollapsed(false);
  }, []);

  const expandRight = useCallback(() => {
    setRightCollapsed(false);
  }, []);

  return {
    leftWidth: leftCollapsed ? COLLAPSED_WIDTH : leftWidth,
    rightWidth: rightCollapsed ? COLLAPSED_WIDTH : rightWidth,
    leftCollapsed,
    rightCollapsed,
    handleLeftResize,
    handleRightResize,
    toggleLeftCollapse,
    toggleRightCollapse,
    expandLeft,
    expandRight,
    // Constants for use in components
    MIN_WIDTH,
    MAX_WIDTH,
    COLLAPSED_WIDTH,
  };
};

export default useResizablePanels;
